import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getPremiumUserIds } from "@/lib/premium.server";

export const listConversations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: convs, error } = await supabase
      .from("conversations")
      .select("id, user_a, user_b, last_message_at")
      .or(`user_a.eq.${userId},user_b.eq.${userId}`)
      .order("last_message_at", { ascending: false });
    if (error) throw new Error(error.message);
    if (!convs || convs.length === 0) return [];

    const otherIds = convs.map((c) => (c.user_a === userId ? c.user_b : c.user_a));
    const ids = convs.map((c) => c.id);
    const [profiles, lastMsgs] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", otherIds),
      supabase
        .from("messages")
        .select("conversation_id, body, created_at, sender_id")
        .in("conversation_id", ids)
        .order("created_at", { ascending: false }),
    ]);
    const pmap = new Map((profiles.data ?? []).map((p) => [p.id, p]));
    const lastByConv = new Map<string, { body: string; created_at: string; sender_id: string }>();
    (lastMsgs.data ?? []).forEach((m) => {
      if (!lastByConv.has(m.conversation_id)) lastByConv.set(m.conversation_id, m);
    });
    const premiumIds = await getPremiumUserIds(otherIds);
    return convs.map((c) => {
      const otherId = c.user_a === userId ? c.user_b : c.user_a;
      const profile = pmap.get(otherId);
      return {
        id: c.id,
        other: profile
          ? { ...profile, is_premium: premiumIds.has(otherId) }
          : { id: otherId, username: "?", display_name: null, avatar_url: null, is_premium: false },
        last_message: lastByConv.get(c.id) ?? null,
        last_message_at: c.last_message_at,
      };
    });
  });

export const listMessages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { conversationId: string }) => i)
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("messages")
      .select("id, sender_id, body, created_at, media_url, media_type")
      .eq("conversation_id", data.conversationId)
      .order("created_at", { ascending: true })
      .limit(500);
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const sendMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { conversationId: string; body?: string; mediaUrl?: string; mediaType?: string }) => {
    const body = (i.body ?? "").trim();
    const mediaUrl = i.mediaUrl?.trim() || null;
    if (!body && !mediaUrl) throw new Error("Mensagem inválida");
    if (body.length > 2000) throw new Error("Mensagem muito longa");
    return { conversationId: i.conversationId, body, mediaUrl, mediaType: mediaUrl ? i.mediaType ?? "gif" : null };
  })
  .handler(async ({ data, context }) => {
    const { error } = await (context.supabase as any).from("messages").insert({
      conversation_id: data.conversationId,
      sender_id: context.userId,
      body: data.body,
      media_url: data.mediaUrl,
      media_type: data.mediaType,
    });
    if (error) throw new Error(error.message);
    await context.supabase
      .from("conversations")
      .update({ last_message_at: new Date().toISOString() })
      .eq("id", data.conversationId);
    return { ok: true };
  });

export const deleteMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { messageId: string }) => i)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("messages")
      .delete()
      .eq("id", data.messageId)
      .eq("sender_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const startConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { otherUserId: string }) => i)
  .handler(async ({ data, context }) => {
    const { data: id, error } = await context.supabase.rpc("ensure_conversation", {
      _other: data.otherUserId,
    });
    if (!error && id) return { conversationId: id as string };

    // A criação pode disputar com outro clique/aba. Nesse caso, reutilize a conversa vencedora.
    if (error?.code === "23505" || error?.message.includes("conversations_user_a_user_b_key")) {
      const userA = context.userId < data.otherUserId ? context.userId : data.otherUserId;
      const userB = context.userId < data.otherUserId ? data.otherUserId : context.userId;
      const { data: existing, error: lookupError } = await context.supabase
        .from("conversations")
        .select("id")
        .eq("user_a", userA)
        .eq("user_b", userB)
        .maybeSingle();
      if (!lookupError && existing?.id) return { conversationId: existing.id };
    }
    throw new Error(error?.message ?? "Não foi possível abrir a conversa");
  });

export const deleteConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { conversationId: string }) => i)
  .handler(async ({ data, context }) => {
    // Delete messages first (no cascade guaranteed)
    await context.supabase.from("messages").delete().eq("conversation_id", data.conversationId);
    const { error } = await context.supabase
      .from("conversations")
      .delete()
      .eq("id", data.conversationId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listChattableFriends = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: rows } = await supabase
      .from("follows")
      .select("following_id")
      .eq("follower_id", userId);
    const ids = (rows ?? []).map((r) => r.following_id);
    if (ids.length === 0) return [];
    const { data: profs } = await supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .in("id", ids);
    const premiumIds = await getPremiumUserIds(ids);
    return (profs ?? []).map((p) => ({ ...p, is_premium: premiumIds.has(p.id) }));
  });
